import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Clock } from 'lucide-react';

interface ActivityRecencyBarProps {
  recency: {
    no_activity_date: number;
    older_than_30d: number;
    days_14_30: number;
    days_7_14: number;
    days_2_7: number;
    last_2_days: number;
  };
}

const SEGMENTS = [
  { key: 'last_2_days' as const, label: 'Last 2 days', color: 'bg-emerald-500', link: '' },
  { key: 'days_2_7' as const, label: '2–7 days', color: 'bg-green-400', link: '' },
  { key: 'days_7_14' as const, label: '7–14 days', color: 'bg-amber-400', link: '' },
  { key: 'days_14_30' as const, label: '14–30 days', color: 'bg-orange-400', link: '/app/leads?stale_days=14' },
  { key: 'older_than_30d' as const, label: '30+ days', color: 'bg-red-400', link: '/app/leads?stale_days=30' },
  { key: 'no_activity_date' as const, label: 'No date', color: 'bg-muted-foreground/40', link: '/app/leads?no_activity=true' },
];

const ActivityRecencyBar = ({ recency }: ActivityRecencyBarProps) => {
  const navigate = useNavigate();
  const total = Object.values(recency).reduce((s, v) => s + v, 0);
  if (total === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-4">
          <CardTitle className="text-base md:text-lg flex items-center gap-2 shrink-0">
            <Clock className="h-4 w-4 text-primary" />
            Outreach Freshness
          </CardTitle>
          <div className="flex flex-wrap justify-end gap-x-4 gap-y-1">
            {SEGMENTS.map(({ key, label, color, link }) => {
              const value = recency[key];
              if (value === 0) return null;
              return (
                <div
                  key={key}
                  className={`flex items-center gap-1.5 text-xs text-muted-foreground ${link ? 'cursor-pointer hover:text-foreground' : ''}`}
                  onClick={link ? () => navigate(link) : undefined}
                >
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${color}`} />
                  {label}: <span className="font-medium text-foreground">{value}</span>
                </div>
              );
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex h-3 md:h-4 rounded-md overflow-hidden">
          {SEGMENTS.map(({ key, color, link }) => {
            const pct = (recency[key] / total) * 100;
            if (pct === 0) return null;
            return (
              <div
                key={key}
                className={`${color} transition-all duration-500 ${link ? 'cursor-pointer hover:opacity-80' : ''}`}
                style={{ width: `${pct}%` }}
                title={`${key}: ${recency[key]}`}
                onClick={link ? () => navigate(link) : undefined}
              />
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ActivityRecencyBar;
